import React, { useState, useMemo, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import qs from 'qs';
import { isEmpty, pick } from 'lodash-es';

import PageNav from './PageNav';
import skeletons from './Skeletons';
import ForecastsTable from './ForecastsTable';
import ForecastsSidebar from './ForecastsSidebar';
import KpiForecastChart from './KpiForecastChart';
import ForecastsCollapsed from './ForecastsCollapsed';
import ForecastsChartLayout from './ForecastsChartLayout';
import {
  ForecastsCompanyHeader,
  ForecastsInsightsHeader,
  ForecastsReportsHeader,
} from './ForecastsHeader';
import Button from '../../../../../components/Buttons/Button';
import SimpleDropdown from '../../../../../components/Dropdowns/SimpleDropdown';
import ConsensusSourceText from '../../../../../components/ConsensusSourceText';

const Forecasts = ({
  forecasts,
  pending,
  activeTab,
  collapsable = false,
  activeQuarter,
  quartersOptions,
  variant,
  allowForecastsWhitoutConsensus,
  availableKpis,
  className,
  makeScreenshotUrl,
  collapsed,
  headerIcon,
  viewButtontext,
  onCollapse,
  onQuarterChange,
  onActiveForecastChange,
}) => {
  const [view, setView] = useState('values');

  const isInsightsPage = variant === 'insights';
  const isReportsPage = variant === 'reports';
  const isCompanyPage = variant === 'company';

  const isForecastsPageWithoutConsensus = !isInsightsPage && allowForecastsWhitoutConsensus;

  const activeForecast = useMemo(() => activeTab
    ? forecasts?.find(forecast => forecast?.kpi_name === activeTab)
    : forecasts?.[0], [forecasts, activeTab]);

  const {
    series,
    data_pack,
    bloomberg_ticker,
    forecast_period,
    forecast_published_at,
    forecast_index,
    final_forecast,
  } = activeForecast?.forecasts || {};

  const activeSeries = !isEmpty(series)
    ? { ...pick(series, ['categories', 'published']), ...series[view] }
    : {};

  const handleTabChange = (tab) => {
    onActiveForecastChange(tab);
  };

  const exportParams = {
    bloomberg_ticker,
    date: data_pack?.date,
    email_id: data_pack?.email_id,
    fy_quarter: forecast_period,
    forecast_published_at,
    forecast_index,
    final_forecast,
  };

  const toggleBlockRef = useRef(null);

  const downloadButton = data_pack?.exists && (
    <Button
      variant="secondary"
      shape={isInsightsPage ? 'square' : undefined}
      disabled={pending}
      href={`/kpi_forecasts/export.zip?${qs.stringify(exportParams)}`}
    >
      {isInsightsPage ? '' : 'Download'}
      <i className="hicon-download-thin" />
    </Button>
  );

  const quartersDropdown = onQuarterChange && (
    <SimpleDropdown
      selected={activeQuarter}
      options={quartersOptions}
      variant="light"
      onChange={onQuarterChange}
    />
  );

  return (
    <div className={cx('card-box forecasts',
      {
        'without-consensus': allowForecastsWhitoutConsensus,
        collapsable,
        collapsed,
      },
      className)}
    >
      {isInsightsPage && (
        <ForecastsInsightsHeader
          pending={pending}
          activeTab={activeTab}
          forecasts={forecasts}
          availableKpis={availableKpis}
          activeForecast={activeForecast}
          collapsable={collapsable}
          headerIcon={headerIcon}
          viewButtontext={viewButtontext}
          collapsed={collapsed}
          onCollapse={onCollapse}
          onTabChange={handleTabChange}
        />
      )}
      {isReportsPage && (
        <ForecastsReportsHeader
          pending={pending}
          activeTab={activeTab}
          forecasts={forecasts}
          availableKpis={availableKpis}
          activeForecast={activeForecast}
          quartersDropdown={quartersDropdown}
          downloadButton={downloadButton}
          onTabChange={handleTabChange}
        />
      )}
      {isCompanyPage && (
        <ForecastsCompanyHeader
          pending={pending}
          activeTab={activeTab}
          forecasts={forecasts}
          availableKpis={availableKpis}
          quartersDropdown={quartersDropdown}
          downloadButton={downloadButton}
          onTabChange={handleTabChange}
        />
      )}
      <div className="card-box__body">
        <div className="toggle-block" ref={toggleBlockRef}>
          {collapsable && (
            <div className={cx('toggle-block-box', { hide: !collapsed, shown: collapsed })}>
              <div className="forecasts-main b-t">
                <div className="animation-fade-element" />
                {pending
                  ? skeletons.forecastCollapsed
                  : (
                    <ForecastsCollapsed
                      forecasts={forecasts}
                    />
                  )}
              </div>
            </div>
          )}
          <div className={cx('toggle-block-box', { hide: collapsed, shown: !collapsed })}>
            {collapsable ? (pending && !collapsed
              ? skeletons.forecastHeader
              : (
                <PageNav
                  pagesList={availableKpis || forecasts}
                  active={activeTab}
                  onChange={handleTabChange}
                />
              )) : null}
            <div className="forecasts-main b-t">
              <ForecastsSidebar
                title={isInsightsPage ? 'Current Forecast' : 'Forecast'}
                activeForecast={activeForecast}
                pending={pending}
                downloadButton={isInsightsPage && downloadButton}
                allowForecastsWhitoutConsensus={allowForecastsWhitoutConsensus}
              />
              <div className="forecasts-main-content">
                <ForecastsChartLayout
                  view={view}
                  title="History"
                  subtitle={pending
                    ? skeletons.forecastSubtitle
                    : (activeForecast?.forecasts?.magnitude
                      ? `${activeForecast.forecasts.kpi_metric_name} (${activeForecast.forecasts.magnitude})`
                      : ''
                  )}
                  disabled={pending}
                  screenshotUrl={makeScreenshotUrl && makeScreenshotUrl({
                    view,
                    kpi: activeForecast?.kpi_name,
                    va_kpi_id: activeForecast?.kpi_id,
                    fy_quarter: activeQuarter,
                    ticker: bloomberg_ticker,
                  })}
                  showSourceText={!allowForecastsWhitoutConsensus}
                  setView={setView}
                >
                  {pending
                    ? skeletons.forecastsMainChart(isForecastsPageWithoutConsensus ? 312 : 193)
                    : (
                      <KpiForecastChart
                        view={view}
                        series={activeSeries}
                        chartHeight={isForecastsPageWithoutConsensus ? 290 : 171}
                        showSourceText={false}
                      />
                    )}
                </ForecastsChartLayout>
                <div className="forecasts-main-content__table">
                  {pending
                    ? skeletons.forecastsMainTable(allowForecastsWhitoutConsensus)
                    : (
                      <ForecastsTable
                        activeForecast={activeForecast}
                      />
                    )}
                </div>
                <div className="forecasts-main-content__footer">
                  <div className="highcharts-watermark" />
                  {!allowForecastsWhitoutConsensus && (
                    <ConsensusSourceText className="forecasts-main-content__footer-description" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Forecasts.propTypes = {
  forecasts: T.array,
  activeTab: T.string,
  collapsable: T.bool,
  pending: T.bool,
  quartersOptions: T.array,
  activeQuarter: T.string,
  variant: T.oneOf(['insights', 'reports', 'company']),
  availableKpis: T.array,
  allowForecastsWhitoutConsensus: T.bool,
  className: T.string,
  collapsed: T.bool,
  onCollapse: T.func,
  headerIcon: T.element,
  makeScreenshotUrl: T.func,
  viewButtontext: T.string,
  onQuarterChange: T.func,
  onActiveForecastChange: T.func.isRequired,
};

export default Forecasts;
